import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalOverlay,
  Select,
  Stack,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';
import { useForm, Controller } from 'react-hook-form';

interface RequestAccessFormValues {
  firstName: string;
  lastName: string;
  workEmail: string;
  jobTitle: string;
  department: string;
  phoneNumber: string;
  reason: string;
  acknowledgement: boolean;
}

interface RequestAccessFormModalProps {
  isOpen: boolean;
  organizationId?: string;
  organizationName?: string;
  membershipType?: string;
  onBack: () => void;
  onClose?: () => void;
  onSubmit: (data: RequestAccessFormValues) => void;
  isSubmitting?: boolean;
  initialValues?: Partial<RequestAccessFormValues>;
  userProfile?: Partial<RequestAccessFormValues>;
}

export const RequestAccessFormModal: React.FC<RequestAccessFormModalProps> = ({
  isOpen,
  organizationName = 'Organization',
  membershipType = 'Company Paid Employee Membership',
  onBack,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialValues,
  userProfile,
}) => {
  const toast = useToast();
  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RequestAccessFormValues>({
    defaultValues: {
      firstName: initialValues?.firstName || '',
      lastName: initialValues?.lastName || '',
      workEmail: initialValues?.workEmail || '',
      jobTitle: initialValues?.jobTitle || '',
      department: initialValues?.department || '',
      phoneNumber: initialValues?.phoneNumber || '',
      reason: initialValues?.reason || '',
      acknowledgement: initialValues?.acknowledgement || false,
    },
  });

  React.useEffect(() => {
    if (isOpen && initialValues) {
      reset({
        firstName: initialValues.firstName || '',
        lastName: initialValues.lastName || '',
        workEmail: initialValues.workEmail || '',
        jobTitle: initialValues.jobTitle || '',
        department: initialValues.department || '',
        phoneNumber: initialValues.phoneNumber || '',
        reason: initialValues.reason || '',
        acknowledgement: initialValues.acknowledgement || false,
      });
    }
  }, [isOpen, initialValues, reset]);

  const handleFormSubmit = async (data: RequestAccessFormValues) => {
    try {
      onSubmit({
        ...data,
        firstName: data.firstName || initialValues?.firstName || '',
        lastName: data.lastName || initialValues?.lastName || '',
        workEmail: data.workEmail || initialValues?.workEmail || '',
        jobTitle: data.jobTitle || initialValues?.jobTitle || '',
        department: data.department || initialValues?.department || '',
        phoneNumber: data.phoneNumber || initialValues?.phoneNumber || '',
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to submit request',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose ?? onBack} isCentered size="lg" scrollBehavior="inside" closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent maxW="800px" maxH="600px" borderRadius="2xl">
        <ModalCloseButton />
        <ModalHeader pt="6">
          <Text fontSize="lg" fontWeight="semibold">
            Request company membership access
          </Text>
          <Text fontSize="sm" color="gray.600" fontWeight="normal" mt="2">
            Complete this form to request access to {organizationName}&apos;s membership
          </Text>
        </ModalHeader>
        <ModalBody pb="6">
          <form id="request-access-form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
            <Stack spacing="4">
  
              <Box bg="blue.50" p="3" borderRadius="md">
                <Stack spacing="2">
                  <Box>
                    <Text fontSize="sm" color="gray.700" fontWeight="semibold">
                      {organizationName}
                    </Text>
                    <Text fontSize="sm" color="gray.600" >
                      {membershipType}
                    </Text>
                  </Box>
                </Stack>
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" mb="3" color="gray.800">
                  Personal Information
                </Text>
                <HStack spacing="4" align="stretch">
                  <FormControl isRequired isInvalid={!!errors.firstName}>
                    <FormLabel fontSize="sm">First Name</FormLabel>
                    <Input
                      {...register('firstName', {
                        required: 'This is Required',
                      })}
                      placeholder="e.g., Sam"
                      isDisabled={!!userProfile?.firstName}
                    />
                    <FormErrorMessage mt="2">
                      {errors.firstName?.message}
                    </FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.lastName}>
                    <FormLabel fontSize="sm">Last Name</FormLabel>
                    <Input
                      {...register('lastName', {
                        required: 'This is Required',
                      })}
                      placeholder="e.g., Taylor"
                      isDisabled={!!userProfile?.lastName}
                    />
                    <FormErrorMessage mt="2">
                      {errors.lastName?.message}
                    </FormErrorMessage>
                  </FormControl>
                </HStack>
              </Box>

              <FormControl isRequired isInvalid={!!errors.workEmail}>
                <FormLabel fontSize="sm">Work Email</FormLabel>
                <Input
                  {...register('workEmail', {
                    required: 'This is Required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  placeholder="sam@greenfieldag.com"
                  type="email"
                  isDisabled={!!userProfile?.workEmail}
                />
                <FormErrorMessage mt="2">{errors.workEmail?.message}</FormErrorMessage>
              </FormControl>

              <HStack spacing="4" align="stretch">
                <FormControl isRequired isInvalid={!!errors.jobTitle}>
                  <FormLabel fontSize="sm">Job Title</FormLabel>
                  <Input
                    {...register('jobTitle', {
                      required: 'This is Required',
                    })}
                    placeholder="e.g., Operations Manager"
                    isDisabled={!!userProfile?.jobTitle}
                  />
                  <FormErrorMessage mt="2">{errors.jobTitle?.message}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={!!errors.phoneNumber}>
                  <FormLabel fontSize="sm">Primary Phone</FormLabel>
                  <Input
                    {...register('phoneNumber', {
                      pattern: {
                        value: /^[0-9]*$/,
                        message: 'Only numbers are allowed',
                      },
                    })}
                    onInput={(e) => {
                      e.currentTarget.value = e.currentTarget.value.replace(/\D/g, '');
                    }}
                    placeholder="(555) 555-0100"
                    type="tel"
                    isDisabled={!!userProfile?.phoneNumber}
                  />
                  <FormErrorMessage mt="2">
                    {errors.phoneNumber?.message}
                  </FormErrorMessage>
                </FormControl>
              </HStack>

              <FormControl isInvalid={!!errors.department}>
                <FormLabel fontSize="sm">Department</FormLabel>
                  <Input
                    {...register('department')}
                    placeholder="Enter your department"
                    isDisabled={!!userProfile?.department}
                  />
                <FormErrorMessage mt="2">{errors.department?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isRequired isInvalid={!!errors.reason}>
                <FormLabel fontSize="sm">Reason for requesting access</FormLabel>
                <Textarea
                  {...register('reason', {
                    required: 'This is Required',
                    minLength: {
                      value: 10,
                      message: 'Reason must be at least 10 characters',
                    },
                  })}
                  placeholder="Briefly explain your role and why you need membership access."
                  rows={3}
                />
                <FormErrorMessage mt="2">{errors.reason?.message}</FormErrorMessage>
              </FormControl>

              <Controller
                name="acknowledgement"
                control={control}
                rules={{ required: 'You must confirm this information' }}
                render={({ field }) => (
                  <FormControl isInvalid={!!errors.acknowledgement}>
                    <Checkbox
                      isChecked={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    >
                      <Text fontSize="sm">
                        I confirm that I am currently affiliated with {organizationName}{' '}
                        and that the information provided is accurate.
                      </Text>
                    </Checkbox>
                    <FormErrorMessage display="block" mt="2">
                      {errors.acknowledgement?.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              />

            </Stack>
          </form>
        </ModalBody>
        <ModalFooter bg="white" borderBottomRadius="2xl" pt="2" pb="4">
          <HStack spacing="3" justify="flex-end">
            <Button variant="outline" onClick={onBack} isDisabled={isSubmitting} colorScheme="blue">
              Back
            </Button>
            <Button
              colorScheme="blue"
              type="submit"
              form="request-access-form"
              isLoading={isSubmitting}
              isDisabled={isSubmitting}
            >
              Review Request
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
